/**
 * Returns the value for a node, prioritizing redacted, then normal, then fallback.
 * @param obj The object to search in
 * @param node The node key to look for
 * @param fallback The fallback value if neither is found
 */
function getNodeValue<T = any>(
  obj: Record<string, T>,
  node: string,
): T | undefined {
  if (!obj) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(obj, node)) {
    return obj[node];
  }

  return node.split(".").reduce<any>((current, segment) => {
    if (!current || typeof current !== "object") {
      return undefined;
    }

    return current[segment];
  }, obj);
}

export function getRedactedOrNodeOrFallback<T = any>(
  obj: Record<string, T>,
  node: string,
  fallback: T,
): T {
  const redactedValue = getNodeValue(obj, `${node}__redacted__`);
  if (redactedValue !== undefined) {
    return redactedValue;
  }

  const value = getNodeValue(obj, node);
  if (value !== undefined) {
    return value;
  }

  return fallback;
}

export function applyRedactedOverrides<T = any>(value: T): T {
  if (!value || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => applyRedactedOverrides(item)) as T;
  }

  const record = value as Record<string, any>;
  const result: Record<string, any> = {};

  for (const key of Object.keys(record)) {
    if (key.endsWith("__redacted__")) {
      continue;
    }

    const redactedKey = `${key}__redacted__`;
    const rawValue = redactedKey in record ? record[redactedKey] : record[key];
    result[key] = applyRedactedOverrides(rawValue);
  }

  return result as T;
}
